import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '@/lib/supabase';

// Shared promise for deduplicating concurrent fetchUserProfile calls
let profileFetchPromise = null;

export const useAuthStore = create(
  persist(
    (set, get) => ({
      // State
      user: null,
      userProfile: null,
      isAuthenticated: false,
      isLoading: false,
      isInitialized: false,
      authType: "home",

      // Actions
      setUser: (user) => {
        console.log("🔧 AuthStore: Setting user", user);
        console.log("🔧 AuthStore: Before set - current state:", get());
        set({ user, isAuthenticated: !!user });
        console.log("🔧 AuthStore: After set - new state:", get());
      },

      // Set user profile
      setUserProfile: (userProfile) => {
        console.log("🔧 AuthStore: Setting user profile", userProfile);
        set({ userProfile });
      },

      // Fetch and set user profile from API (deduplicated — concurrent calls share one request)
      fetchUserProfile: async () => {
        if (profileFetchPromise) {
          return profileFetchPromise;
        }

        // Only show loading spinner if not yet initialized (first load)
        const wasInitialized = get().isInitialized;
        if (!wasInitialized) {
          set({ isLoading: true });
        }

        profileFetchPromise = (async () => {
          try {
            const { apiClient } = await import('@/lib/api');
            const profile = await apiClient.getUserProfile();
            console.log("✅ AuthStore: User profile fetched:", profile);
            set({ userProfile: profile, isLoading: false });
            return profile;
          } catch (error) {
            console.error("❌ AuthStore: Error fetching user profile:", error);
            set({ isLoading: false });
            return null;
          } finally {
            profileFetchPromise = null;
          }
        })();

        return profileFetchPromise;
      },

      // Sync with Supabase session
      syncWithSupabase: async () => {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          console.log("🔄 AuthStore: Syncing with Supabase session", session);

          if (session?.user) {
            set({
              user: session.user,
              isAuthenticated: true
              // Don't set isLoading: false here - let fetchUserProfile handle it
            });
            // Fetch user profile after successful auth
            await get().fetchUserProfile();
          } else {
            set({
              user: null,
              userProfile: null,
              isAuthenticated: false,
              isLoading: false
            });
          }
        } catch (error) {
          console.error("❌ AuthStore: Error syncing with Supabase", error);
          set({ isLoading: false });
        }
      },
      
      // Initialize store with current session
      initialize: async () => {
        // Only show loading spinner on first initialization
        if (!get().isInitialized) {
          set({ isLoading: true });
        }
        await get().syncWithSupabase();
        set({ isInitialized: true });

        // Set up auth state change listener
        supabase.auth.onAuthStateChange(async (event, session) => {
          console.log("🔄 AuthStore: Auth state changed", event, session);

          if (session?.user) {
            // Set loading true for SIGNED_IN to prevent redirect before profile is fetched
            const shouldFetchProfile = event === 'SIGNED_IN';
            set({
              user: session.user,
              isAuthenticated: true,
              isLoading: shouldFetchProfile
            });
            // Fetch user profile after OAuth sign-in
            if (shouldFetchProfile) {
              await get().fetchUserProfile();
            }
          } else {
            set({
              user: null,
              userProfile: null,
              isAuthenticated: false
              // Don't automatically set authType to "home" - let LayoutProvider handle it
            });
          }
        });
      },
      setLoading: (isLoading) => {
        console.log("🔧 AuthStore: Setting loading", isLoading);
        set({ isLoading });
      },
      logout: () => {
        console.log("🔧 AuthStore: Logging out");
        // Clear booking confirmation keys from localStorage
        Object.keys(localStorage).forEach((key) => {
          if (key.startsWith('booking_confirmed_') || key.startsWith('flights_created_')) localStorage.removeItem(key);
        });
        set({ user: null, isAuthenticated: false, authType: "home" });
      },
      setAuthType: (authType) => {
        console.log("🔧 AuthStore: Setting authType", authType);
        set({ authType });
      },
      
      // Getters
      getUser: () => get().user,
      isLoggedIn: () => get().isAuthenticated,
      getAuthType: () => get().authType,
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user, userProfile: state.userProfile, isAuthenticated: state.isAuthenticated, authType: state.authType }),
      onRehydrateStorage: () => (state) => {
        console.log("🔄 AuthStore: Rehydrating from localStorage", state);
      },
    }
  )
);
